import { ENUM_ClientUserType, ENUM_PropertyInvestmentType } from '@api-new/common';
import { HTTP_CP_Company } from '@api-new/companyservice';
import { HTTP_CP_ClientUser } from '@api-new/userservice';
import { environment } from '@environments/environment';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AddSmartSearchPropertyMortgageSelectItemModel } from '@platform/smart-search/models/add-smart-search-property-mortgage-select-item.model';
import { PropertyMortgageSelectItemModel } from '@shared-lib/models/property-mortgage-select-item.model';
import { selectClientUser } from '@shared/store/client-user/client-user.selectors';
import { CommonState } from '@shared/store/common.state';
import { selectCompany } from '@shared/store/company/company.selectors';
import { selectIsLoadingLenders, selectLenderEntities } from '@shared/store/lender/lenders.selectors';
import { selectIsLoadingMortgages, selectMortgages } from '@shared/store/mortgages/mortgages.selectors';
import { selectIsLoadingProperties, selectProperties } from '@shared/store/properties/properties.selectors';

export const selectCommonState = createFeatureSelector<CommonState>('common');

export const selectIsInitialPlatformDataRequested = createSelector(
  selectCommonState,
  (state: CommonState): boolean => state.isInitialPlatformDataRequested,
);

export const selectPropertyMortgageSelectItems = createSelector(
  selectIsLoadingProperties,
  selectIsLoadingMortgages,
  selectIsLoadingLenders,
  selectProperties,
  selectMortgages,
  selectLenderEntities,
  (isLoadingProperties, isLoadingMortgages, isLoadingLenders, properties, mortgages, lenderEntities): PropertyMortgageSelectItemModel[] => {
    if (isLoadingProperties || isLoadingMortgages || isLoadingLenders || properties.length === 0) {
      return [];
    }
    const propertyMortgageSelectItems: PropertyMortgageSelectItemModel[] = properties.map((property) => ({
      propertyId: property.id,
      propertyName: property.name,
      propertyAddressLines: property.addressLines,
      propertyValue: property.roundedCurrentValue,
      isPropertyResidential: property.investmentType === ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_RESIDENTIAL,
    }));

    mortgages.forEach((mortgage) => {
      const item = propertyMortgageSelectItems.find(
        (propertyMortgageSelectItem) => propertyMortgageSelectItem.propertyId === mortgage.propertyId,
      );
      if (!item) {
        return;
      }
      item.mortgageId = mortgage.id;
      item.mortgageRoundedOutstandingBalance = mortgage.roundedCurrentOutstandingBalance;
      item.mortgageLenderLogo = lenderEntities[mortgage.lenderId]?.logo;
      item.mortgageCurrentMonthlyPayment = mortgage.currentMonthlyPayment;
      item.mortgageTermRemaining = mortgage.termRemaining;
    });

    return propertyMortgageSelectItems;
  },
);

export const selectPropertyMortgageSelectItemsForAddSmartSearch = createSelector(
  selectIsLoadingProperties,
  selectIsLoadingMortgages,
  selectIsLoadingLenders,
  selectProperties,
  selectMortgages,
  selectLenderEntities,
  (
    isLoadingProperties,
    isLoadingMortgages,
    isLoadingLenders,
    properties,
    mortgages,
    lenderEntities,
  ): AddSmartSearchPropertyMortgageSelectItemModel[] => {
    if (isLoadingProperties || isLoadingMortgages || isLoadingLenders || properties.length === 0) {
      return [];
    }

    return properties.map((property) => {
      const mortgage = mortgages.find((mortgage) => mortgage.propertyId === property.id);
      return {
        propertyId: property.id,
        propertyName: property.name,
        propertyAddressLines: property.addressLines,
        propertyValue: property.roundedCurrentValue,
        isPropertyResidential: property.investmentType === ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_RESIDENTIAL,
        ...(mortgage && {
          mortgageId: mortgage.id,
          mortgageLenderLogo: lenderEntities[mortgage.lenderId]?.logo,
          mortgageRoundedOutstandingBalance: mortgage.roundedCurrentOutstandingBalance,
          mortgageType: mortgage.mortgageType,
          mortgageCurrentMonthlyPayment: mortgage.currentMonthlyPayment,
          mortgageTermRemaining: mortgage.termRemaining,
        }),
      };
    });
  },
);

export const selectIsLoadingPlatformItems = createSelector(
  selectIsLoadingProperties,
  selectIsLoadingMortgages,
  selectIsLoadingLenders,
  (isLoadingProperties, isLoadingMortgages, isLoadingLenders): boolean => isLoadingLenders && isLoadingMortgages && isLoadingLenders,
);

export const selectIsClientUserMABLandlord = createSelector(
  selectClientUser,
  selectCompany,
  (clientUser: HTTP_CP_ClientUser, company: HTTP_CP_Company) =>
    clientUser?.clientUserType === ENUM_ClientUserType.CLIENT_USER_TYPE_LANDLORD &&
    company?.networkId === environment.companyAndNetworkIds.mabNetworkId,
);
